import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableHeadCell,
  TableDots,
  Pagination,
  Chip,
  Button,
  gateModal,
  enqueueSnackbar,
  Checkbox,
  gateDeleteModal,
  TableIconsCell,
  SimpleTable,
} from '../../design-system';
import { DashboardHeader } from '../dashboard';
import { Filters } from './Filters';
import { useStore } from '../../context';
import { getGateInStatus, isShowDots } from './utils';
import { filterWithUserId, formatContainerType, momentTextFormat, showToRoles } from '../../utils';
import {
  usePaginationState,
  useGateInsQuery,
  useCompleteGateInMutation,
  useStatesWithFilterCounter,
  useSort,
} from '../../hooks';
import {
  gateTableDotsActions,
  TABLE,
  MESSAGES,
  GateStatusToChipLabels,
  GateStatusToChipColors,
  ContainerConditionToChipColors,
  ContainerConditionToChipLabels,
} from '../../const';
import { GateInGetAllQuery } from '../../types';
import { i18n } from '../../i18n';
import { handleExportExcelClick } from './handle-export-excel-click';

interface GateInsPageProps {
  innerScroll?: boolean;
}

export const GateInsPage = styled.div<GateInsPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

export const GateInsHeader = ({ totalCount, handleExportExcelClick }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.gateIns.title')}: {totalCount}
      </Typography>
      <Button size={'medium'} onClick={handleExportExcelClick} disabled={!totalCount}>
        {i18n.t('button.exportExcel')}
      </Button>
      {!showToRoles(['YARD_COMPANY_VIEWER'], user) && (
        <Button size={'medium'} to={'/dashboard/gateins/add'}>
          {i18n.t('button.createRequest')}
        </Button>
      )}
    </DashboardHeader>
  );
};

interface CheckboxActionsProps {
  data: GateInGetAllQuery['data'];
  refetch: () => void;
  checkIndexs: number[];
  setCheckIndexs: (indexes: number[]) => void;
}

export const CheckboxActions = ({
  data,
  checkIndexs,
  setCheckIndexs,
  refetch,
}: CheckboxActionsProps) => {
  const handleClickAction = (val: any) => {
    if (val === 'delete') {
      const validatedData = data
        .filter((_: any, i: number) => checkIndexs.includes(i))
        .filter((item) => item.gateIn.status === 'ACTIVE');

      if (!validatedData.length) {
        return enqueueSnackbar(MESSAGES.ACTIONS.NOT_VALID, 'warning');
      }

      gateDeleteModal.data = validatedData.map((item) => ({
        id: item.id,
        number: item.number,
      }));
      gateDeleteModal.isGateIn = true;

      gateDeleteModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.GATE_IN.SUCCESS.DELETED, 'success');
        refetch();
      };
      gateDeleteModal.open();
    }
  };

  if (!checkIndexs.length) return <></>;

  return (
    <>
      <Button size={'medium'} data-color={'white'} onClick={() => handleClickAction('delete')}>
        {i18n.t('button.delete')}
      </Button>
    </>
  );
};

export const GateInsBody = ({
  setData,
  setTotalCount,
  checkIndexs,
  setCheckIndexs,
  handleCheckboxChange,
  setExportHandler,
  onPrintReceipt,
}: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const navigate = useNavigate();
  const completeMutation = useCompleteGateInMutation();

  const [states, setStates, filteredStates] = useStatesWithFilterCounter(
    {
      containerNumber: '',
      containerType: 'All',
      yardCompanyId: '',
      country: '',
      city: '',
      yardId: '',
      clientId: '',
      createdAt: null,
      startDate: null,
      endDate: null,
      completedAt: null,
      licensePlateNumber: '',
      status: 'All',
    },
    { status: 'ACTIVE' },
  );
  const { sortField, sortDirection, handleSort } = useSort({
    sortField: filteredStates.status === 'COMPLETED' ? 'completedAt' : 'createdAt',
    sortDirection: 'desc',
  });

  const { page, take, skip, handlePageChange } = usePaginationState();
  const [openDotsMenuIndex, setDotsMenuIndex] = useState(null);

  const {
    data = { data: [], totalCount: 0 },
    isFetching,
    refetch,
  } = useGateInsQuery({
    ...filterWithUserId(filteredStates, user),
    onlyActive: states.status === 'ACTIVE' ? 'true' : 'false',
    take,
    skip,
    sortField,
    sortDirection,
  });

  useEffect(() => {
    if (!setExportHandler) return;
    setExportHandler(
      () => () =>
        handleExportExcelClick({
          states,
          user,
          filteredStates,
          totalCount: data.totalCount,
          sortField,
          sortDirection,
        }),
    );
    return () => setExportHandler(null);
  }, [setExportHandler, states, user, filteredStates, data.totalCount, sortField, sortDirection]);

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
    setData(data.data);
  }, [isFetching]);

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);

    setDotsMenuIndex(null);
  };

  const handleOnPageChange = (e: any, page: number) => {
    if (checkIndexs.length) setCheckIndexs([]);
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handleOnPageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = async (id: any, val: any, i: any) => {
    Object.keys(gateModal).forEach((key) => {
      if (key !== 'open') delete gateModal[key];
    });

    gateModal.val = val;
    gateModal.containerNumber = data.data[i].number;

    if (val === 'complete') {
      gateModal.clientYardId = data.data[i].clientYardId;
      gateModal.type = 'GATE_IN';
      gateModal.containerType = data.data[i].type;
      gateModal.condition = data.data[i].condition;
      gateModal.driverFirstName = data.data[i].gateIn.driver?.firstName;
      gateModal.driverLastName = data.data[i].gateIn.driver?.lastName;
      gateModal.licensePlateNumber = data.data[i].gateIn.driver?.licensePlateNumber;
      gateModal.fc = (printAfterComplete = false) =>
        completeMutation.mutate(id, {
          onSuccess: () => {
            setCheckIndexs([]);
            enqueueSnackbar(MESSAGES.GATE_IN.SUCCESS.COMPLETED, 'success');
            refetch();
            if (printAfterComplete) onPrintReceipt(id);
          },
          onError: () => enqueueSnackbar(MESSAGES.GATE_IN.ERROR.NOT_COMPLETED, 'error'),
        });
    }
    if (val === 'delete') {
      gateDeleteModal.data = [
        {
          id,
          number: data.data[i].number,
        },
      ];
      gateDeleteModal.isGateIn = true;

      gateDeleteModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.GATE_IN.SUCCESS.DELETED, 'success');
        refetch();
      };
      gateDeleteModal.open();
      return;
    }

    gateModal.open();
  };

  const handleRowClick = (to: string) => {
    if (openDotsMenuIndex !== null) return;
    if (checkIndexs.length) return;
    navigate(to);
  };

  return (
    <SimpleTable
      data={data}
      filters={() => <Filters handleSort={handleSort} setStates={handleFilterChange} {...states} />}
      checkboxActions={
        showToRoles('admin', user)
          ? (data) => (
              <CheckboxActions
                checkIndexs={checkIndexs.filter(
                  (i: number) => data[i]?.gateIn?.status === 'ACTIVE',
                )}
                data={data}
                refetch={refetch}
                setCheckIndexs={setCheckIndexs}
              />
            )
          : undefined
      }
      tableRowProps={(container, i) => ({
        key: container.number + i,
        onClick: () => handleRowClick(`/dashboard/gateins/${container.id}`),
      })}
      columns={[
        {
          column: 1,
          isHidden: () => !showToRoles('adminRead', user),
          head: () => (
            <TableHeadCell align={'left'} withGap>
              {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN'], user) && (
                <Checkbox
                  indeterminate={!!checkIndexs.length && checkIndexs.length !== data.data.length}
                  checked={checkIndexs.length === data.data.length}
                  onChange={() => handleCheckboxChange()}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {i18n.t('label.actions')}
            </TableHeadCell>
          ),
          body: (container, i) => (
            <TableIconsCell component={'th'} align={'left'}>
              {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN'], user) && (
                <Checkbox
                  checked={checkIndexs.includes(i)}
                  onChange={() => handleCheckboxChange(i)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {isShowDots(container.gateIn, user) && (
                <TableDots
                  open={openDotsMenuIndex === i}
                  onToggle={() => handleDotsToggle(i)}
                  options={
                    showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN'], user) &&
                    container.gateIn.status === 'ACTIVE'
                      ? [
                          ...gateTableDotsActions,
                          { label: i18n.t('dropdown.delete'), value: 'delete' },
                        ]
                      : gateTableDotsActions
                  }
                  onSelect={(val) => {
                    handleDotsOptionSelect(container.id, val, i);
                  }}
                />
              )}
            </TableIconsCell>
          ),
        },
        {
          headText: () => i18n.t('common.client'),
          bodyText: (container) => container.clientYard?.client.code,
          isHidden: () => !showToRoles('admin', user),
        },
        {
          headText: () => i18n.t('label.containerNumber'),
          body: (container) => (
            <TableCell component={showToRoles('client', user) ? 'th' : 'td'} align={'left'}>
              {container.number}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.type'),
          bodyText: (container) => formatContainerType(container.type),
        },
        {
          headText: () => i18n.t('label.condition'),
          body: (container) => (
            <TableCell align={'left'}>
              {container.condition && (
                <Chip
                  size={'small'}
                  label={ContainerConditionToChipLabels[container.condition]}
                  color={ContainerConditionToChipColors[container.condition]}
                />
              )}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.status'),
          body: (container) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={
                  GateStatusToChipLabels[
                    getGateInStatus(container.gateIn.validTo, container.gateIn.status)
                  ]
                }
                color={
                  GateStatusToChipColors[
                    getGateInStatus(container.gateIn.validTo, container.gateIn.status)
                  ]
                }
              />
            </TableCell>
          ),
        },
        {
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'createdAt'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.createdAt')}
            </TableHeadCell>
          ),
          bodyText: (container) =>
            container.gateIn.createdAt ? momentTextFormat(container.gateIn.createdAt, user) : '',
        },
        {
          isHidden: () => !(states.status === 'All' || states.status === 'COMPLETED'),
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'completedAt'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.completedAt')}
            </TableHeadCell>
          ),
          bodyText: (container) =>
            container.gateIn.completedAt
              ? momentTextFormat(container.gateIn.completedAt, user)
              : '',
        },
        {
          headText: () => i18n.t('label.gateInDateFrom'),
          bodyText: (container) =>
            container.gateIn.validFrom
              ? momentTextFormat(container.gateIn.validFrom, user)
              : (states.status === 'All' || states.status === 'COMPLETED') &&
                container.gateIn.createdAt
              ? momentTextFormat(container.gateIn.createdAt, user)
              : '',
        },
        {
          headText: () => i18n.t('label.gateInDateTo'),
          bodyText: (container) =>
            container.gateIn.validTo
              ? momentTextFormat(container.gateIn.validTo, user)
              : (states.status === 'All' || states.status === 'COMPLETED') &&
                container.gateIn.completedAt
              ? momentTextFormat(container.gateIn.completedAt, user)
              : '',
        },
        {
          isHidden: () => states.status === 'ACTIVE' || states.status === 'DELETED',
          headText: () => i18n.t('label.licensePlateNumber'),
          bodyText: (container) => container.gateIn.driver?.licensePlateNumber,
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handleOnPageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
